import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { showBotTypeSelectionDialog } from '~/features/bots/components/BotTypeSelectionDialog'
import { useFeature } from '~/hooks/useFeature'
import { setWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { config } from '~/shared'
import { useBotCreationStore } from '~/stores'
import { useVariant } from '@unleash/proxy-client-react'

type UseCreateBotFlowProps = {
  workspaceId: string | undefined
}

export const useCreateBotFlow = ({ workspaceId }: UseCreateBotFlowProps) => {
  const navigate = useNavigate()
  const createBot = useBotCreationStore((state) => state.createBot)
  const createAdkBot = useBotCreationStore((state) => state.createAdkBot)
  const [isCreating, setIsCreating] = useState(false)
  const isAdkBotsEnabled = useFeature('adk-bots-from-dashboard')
  const isStudioOnboardingEnabled = useFeature('studio-onboarding')
  const studioOnboardingVariant = useVariant('studio-onboarding')

  const createStudioBot = (wsId: string) => {
    setIsCreating(true)
    createBot({
      workspaceId: wsId,
      entrypoint: 'new_bot',
      skipDashboardSetup: isStudioOnboardingEnabled && studioOnboardingVariant.name !== 'dashboard',
      onBotCreated: async (bot) => {
        if (isStudioOnboardingEnabled && studioOnboardingVariant.name !== 'dashboard') {
          await setWorkspacePreference(
            {
              path: 'isStudioOnboarding/$botId',
              params: { botId: bot.id },
              value: {
                entryPoint: 'new_bot',
                version: studioOnboardingVariant.name ?? 'default',
              },
            },
            wsId
          )
          window.location.href = `${config.studioBaseUrl}/${bot.id}`
        } else {
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/new-bot',
            params: { workspaceId: wsId, botId: bot.id },
          })
        }
      },
      onBotError: () => setIsCreating(false),
    })
  }

  const startCreateBotFlow = async () => {
    if (!workspaceId) {
      return
    }

    // If ADK bots feature is disabled, skip the selection dialog and create a Studio bot directly
    if (!isAdkBotsEnabled) {
      createStudioBot(workspaceId)
      return
    }

    const botType = await showBotTypeSelectionDialog()
    if (!botType) {
      return
    }

    if (botType === 'studio') {
      createStudioBot(workspaceId)
    } else if (botType === 'adk') {
      setIsCreating(true)

      // Navigate to loading screen immediately
      navigate({
        to: '/workspaces/$workspaceId/loading-bot',
        params: { workspaceId },
      })

      // Create bot async - will redirect to setup page via $botId.tsx redirect logic
      createAdkBot({
        workspaceId,
        name: 'New ADK Agent',
        onBotCreated: (bot) => {
          navigate({
            to: '/workspaces/$workspaceId/bots/$botId/new-adk-bot-setup',
            params: { workspaceId, botId: bot.id },
          })
        },
        onBotError: () => {
          setIsCreating(false)
          // Navigate back to home on error
          navigate({
            to: '/workspaces/$workspaceId/home',
            params: { workspaceId },
          })
        },
      })
    }
  }

  return { startCreateBotFlow, isCreatingBot: isCreating }
}
